import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import { inputCls, btnPrimary, btnSecondary, fmtDate } from '../types'
import CustomerCombobox from '../../../components/CustomerCombobox'

export default function UploadLoadingList() {
  const { id } = useParams<{ id: string }>()
  const { userEmail } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefillJobRef     = searchParams.get('job_ref')     ?? ''
  const prefillContainer  = searchParams.get('container_no') ?? ''
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dupTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  type DupRecord = { id: string; file_name: string | null; created_at: string }
  const [dupCheck, setDupCheck] = useState<DupRecord | 'checking' | null>(null)

  const ACCEPTED = ['.pdf', '.xlsx', '.xls', '.csv']
  const acceptFile = (f: File) => {
    const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase()
    if (ACCEPTED.includes(ext)) setFile(f)
    else setError(`Unsupported file type. Please use: ${ACCEPTED.join(', ')}`)
  }
  const [form, setForm] = useState({ job_ref: prefillJobRef.toUpperCase(), source_customer_id: '', container_no: prefillContainer.toUpperCase(), truck_plate: '', transport_mode: 'sea' as 'sea' | 'truck' | 'air', notes: '', expected_total_cartons: '', expected_total_weight_kg: '', expected_total_cbm: '', container_eta_at: '', free_demurrage_days: '5' })
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  // Run dup check on pre-filled job_ref from URL params
  useEffect(() => { if (prefillJobRef) checkJobRefDup(prefillJobRef) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const checkJobRefDup = (raw: string) => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current)
    const ref = raw.trim()
    if (ref.length < 3) { setDupCheck(null); return }
    setDupCheck('checking')
    dupTimerRef.current = setTimeout(async () => {
      const { data } = await supabase.from('loading_lists')
        .select('id, file_name, created_at')
        .eq('job_ref', ref)
        .maybeSingle()
      setDupCheck((data as DupRecord) || null)
    }, 400)
  }

  useEffect(() => () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current) }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const ref = form.job_ref.trim()
    if (!ref) { setError('Job Ref is required.'); return }
    if (!/^(KI|KE)/i.test(ref)) { setError('Job Ref must start with KI or KE.'); return }
    if (dupCheck && dupCheck !== 'checking') { setError(`Job Ref '${ref}' already exists. View or delete the existing record first.`); return }
    if (form.transport_mode === 'sea' && form.container_no && !/^[A-Za-z]{4}\d{7}$/.test(form.container_no.trim())) {
      setError('Container No. must be 4 letters + 7 digits (e.g. TCKU3456789)'); return
    }
    setUploading(true); setError(null)
    try {
      let file_url: string | null = null
      let file_name: string | null = null
      let file_type: string | null = null

      if (file) {
        const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : ''
        const sanitized = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_')
        const path = `${id}/${Date.now()}_${sanitized}`
        const { error: upErr } = await supabase.storage.from('warehouse-files').upload(path, file)
        if (upErr) throw upErr
        const { data: { publicUrl } } = supabase.storage.from('warehouse-files').getPublicUrl(path)
        file_url = publicUrl
        file_name = file.name
        file_type = file.type || (ext === '.xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : ext === '.xls' ? 'application/vnd.ms-excel' : 'application/octet-stream')
      }

      const { data, error: dbErr } = await supabase.from('loading_lists')
        .insert({
          warehouse_id: id,
          job_ref: ref || null,
          source_customer_id: form.source_customer_id || null,
          transport_mode: form.transport_mode,
          container_no: form.transport_mode !== 'truck' ? (form.container_no.trim() || null) : null,
          truck_plate: form.transport_mode === 'truck' ? (form.truck_plate.trim() || null) : null,
          notes: form.notes || null,
          file_url,
          file_name,
          file_type,
          parse_status: 'manual',
          uploaded_by: userEmail,
          expected_total_cartons: form.expected_total_cartons ? parseInt(form.expected_total_cartons) : null,
          expected_total_weight_kg: form.expected_total_weight_kg ? parseFloat(form.expected_total_weight_kg) : null,
          expected_total_cbm: form.expected_total_cbm ? parseFloat(form.expected_total_cbm) : null,
          container_eta_at: form.container_eta_at ? new Date(form.container_eta_at).toISOString() : null,
          free_demurrage_days: form.free_demurrage_days ? parseInt(form.free_demurrage_days) : 5,
        })
        .select('id')
        .single()
      if (dbErr) throw dbErr

      // Link any pending plan that has a matching job_ref but no LL yet
      let linkedPlanId: string | null = null
      if (ref && id) {
        const { data: linked } = await supabase
          .from('devanning_plans')
          .update({ source_loading_list_id: data.id })
          .eq('job_ref', ref)
          .eq('warehouse_id', id)
          .is('source_loading_list_id', null)
          .select('id')
          .maybeSingle()
        linkedPlanId = (linked as any)?.id ?? null
      }

      // Always go to the LL detail/parse page.
      // If a plan was linked, it's already updated in the DB; the user can
      // navigate to the plan from the LL detail after parsing is done.
      void linkedPlanId  // suppress unused-variable lint
      navigate(`/warehouse/${id}/loading-lists/${data.id}`)
    } catch (err: any) {
      if (err.code === '23505') {
        setError(`Job Ref '${form.job_ref.trim()}' already exists. Please use a unique Job Ref.`)
      } else {
        setError(err.message || 'Upload failed')
      }
      setUploading(false)
    }
  }

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="max-w-xl">
        <Link to={`/warehouse/${id}/loading-lists`} className="text-sm text-blue-600 hover:underline mb-4 block">← Loading Lists</Link>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Upload Loading List</h2>
        {prefillJobRef && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
            Uploading for pending plan <strong>{prefillJobRef}</strong>
            {prefillContainer && <> · <span className="font-mono">{prefillContainer}</span></>}.
            {' '}Job Ref and Container have been pre-filled.
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">

          {/* Job Ref */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Job Ref <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
                  dupCheck && dupCheck !== 'checking'
                    ? 'border-red-500 dark:border-red-500 focus:ring-red-400'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }`}
                placeholder="e.g. KI17875 or KE12345"
                maxLength={10}
                value={form.job_ref}
                onChange={e => {
                  const v = e.target.value.toUpperCase()
                  set('job_ref', v)
                  checkJobRefDup(v)
                }}
                onBlur={() => checkJobRefDup(form.job_ref)}
              />
              {dupCheck === 'checking' && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">checking…</span>
              )}
            </div>
            {dupCheck && dupCheck !== 'checking' && (
              <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
                ⚠ Job Ref &lsquo;{form.job_ref}&rsquo; already exists
                {dupCheck.file_name ? ` (${dupCheck.file_name})` : ''} — uploaded {fmtDate(dupCheck.created_at)}.{' '}
                <Link to={`/warehouse/${id}/loading-lists/${dupCheck.id}`} className="underline font-medium">
                  View existing →
                </Link>
              </p>
            )}
          </div>

          {/* File */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File (optional)</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault()
                setDragOver(false)
                const dropped = e.dataTransfer.files[0]
                if (dropped) acceptFile(dropped)
              }}
              className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-colors select-none
                ${dragOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.csv"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) acceptFile(f) }}
              />
              {file ? (
                <>
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{file.name}</span>
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >Remove</button>
                </>
              ) : (
                <>
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {dragOver ? 'Drop file here' : 'Drag & drop or click to choose'}
                  </span>
                  <span className="text-xs text-gray-400">PDF, Excel or CSV</span>
                </>
              )}
            </div>
          </div>

          {/* Source Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Customer</label>
            <CustomerCombobox value={form.source_customer_id} onChange={v => set('source_customer_id', v)} placeholder="— Select customer (optional) —" />
          </div>

          {/* Transport Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Transport Mode</label>
            <div className="flex gap-2">
              {(['sea', 'truck', 'air'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => set('transport_mode', mode)}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors capitalize ${
                    form.transport_mode === mode
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {mode === 'sea' ? '🚢 Sea' : mode === 'truck' ? '🚛 Truck' : '✈️ Air'}
                </button>
              ))}
            </div>
          </div>

          {/* Reference field — label/validation changes per transport mode */}
          <div>
            {form.transport_mode === 'sea' && (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Container No.</label>
                <input className={inputCls} value={form.container_no}
                  onChange={e => set('container_no', e.target.value.toUpperCase())}
                  placeholder="e.g. TCKU3456789 (4 letters + 7 digits)"
                  maxLength={11} />
                {form.container_no && !/^[A-Za-z]{4}\d{7}$/.test(form.container_no) && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Format: 4 letters + 7 digits</p>
                )}
              </>
            )}
            {form.transport_mode === 'truck' && (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Truck Plate</label>
                <input className={inputCls} value={form.truck_plate}
                  onChange={e => set('truck_plate', e.target.value.toUpperCase())}
                  placeholder="e.g. KI17875M" />
              </>
            )}
            {form.transport_mode === 'air' && (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AWB No. <span className="text-red-500">*</span></label>
                <input className={inputCls} value={form.container_no}
                  onChange={e => set('container_no', e.target.value)}
                  placeholder="e.g. 123-45678901" />
              </>
            )}
          </div>

          {/* Expected Totals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expected Total Cartons <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input type="number" min="0" step="1" className={inputCls} placeholder="e.g. 720"
              value={form.expected_total_cartons} onChange={e => set('expected_total_cartons', e.target.value)} />
          </div>

          {/* Container ETA + Free Demurrage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Container ETA &amp; Free Demurrage <span className="text-gray-400 font-normal">(optional — for overdue alerts)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Container ETA</label>
                <input type="date" className={inputCls} value={form.container_eta_at} onChange={e => set('container_eta_at', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Free Demurrage Days</label>
                <input type="number" min="0" max="30" className={inputCls} value={form.free_demurrage_days} onChange={e => set('free_demurrage_days', e.target.value)} />
              </div>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Link to={`/warehouse/${id}/loading-lists`} className={btnSecondary}>Cancel</Link>
            <button type="submit" disabled={uploading || (dupCheck !== null && dupCheck !== 'checking')} className={btnPrimary}>{uploading ? 'Saving...' : 'Save & Edit Items'}</button>
          </div>
        </form>
      </div>
    </WarehouseLayout>
  )
}
